import type { PortalProfile } from "@repo/edge/types";
export const EDGE_URL = (import.meta.env.VITE_EDGE_URL || "http://localhost:8105").replace(/\/$/, "");
interface Tokens {
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}
interface StoredTokens extends Tokens {
  expiresAt: number;
}
const KEY = "patient-edge-tokens";
let profile: PortalProfile | undefined;
let pendingMutations = 0;
const selectionKey = (actor: string) => `patient-edge-selection:${actor}`;

let tokens: StoredTokens | undefined;
let stored: string | null | undefined;
function syncTokens() {
  const next = localStorage.getItem(KEY);
  if (next === stored) return;
  stored = next;
  try {
    tokens = JSON.parse(next ?? "null") ?? undefined;
  } catch {
    tokens = undefined;
  }
  if (!tokens) profile = undefined;
}
syncTokens();
window.addEventListener("storage", (event) => {
  if ((event.key === KEY || event.key === null) && (!event.storageArea || event.storageArea === localStorage)) {
    syncTokens();
    for (const listener of listeners) listener();
  }
});
let refreshing: Promise<void> | undefined;
const listeners = new Set<() => void>();
export const session = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  getProfile: () => profile,
  getPendingMutations: () => pendingMutations,
  trackMutation() {
    pendingMutations++;
    for (const listener of listeners) listener();
    let finished = false;
    return () => {
      if (finished) return;
      finished = true;
      pendingMutations--;
      for (const listener of listeners) listener();
    };
  },
  selectPatient(patientId: string) {
    const patient = profile?.patients.find((entry) => entry.patientId === patientId);
    if (!profile || !patient) throw new Error("This patient is not available to your account");
    profile = { ...profile, selectedPatientId: patient.patientId };
    localStorage.setItem(selectionKey(profile.actor.reference), patientId);
    for (const listener of listeners) listener();
  },
  hasTokens: () => !!tokens,
  setProfile(value: PortalProfile) {
    const remembered = localStorage.getItem(selectionKey(value.actor.reference));
    const patient = value.patients.find((entry) => entry.patientId === remembered);
    profile = patient ? { ...value, selectedPatientId: patient.patientId } : value;
    for (const listener of listeners) listener();
  },
  clear() {
    tokens = undefined;
    profile = undefined;
    localStorage.removeItem(KEY);
    stored = null;
    for (const listener of listeners) listener();
  },
  accept(value: Tokens, preserveRefresh = false) {
    if (!preserveRefresh) profile = undefined;
    if (!value.access_token || !Number.isFinite(value.expires_in)) throw new Error("Invalid sign-in response");
    tokens = {
      ...value,
      refresh_token: value.refresh_token ?? (preserveRefresh ? tokens?.refresh_token : undefined),
      expiresAt: Date.now() + value.expires_in * 1000,
    };
    stored = JSON.stringify(tokens);
    localStorage.setItem(KEY, stored);
  },
  async accessToken(rejectedToken?: string): Promise<string | undefined> {
    syncTokens();
    const needsRefresh = () =>
      tokens && (tokens.expiresAt < Date.now() + 30_000 || tokens.access_token === rejectedToken);
    if (needsRefresh()) {
      if (!refreshing) {
        // Web Locks serialize rotating refresh tokens across same-origin tabs.
        // Re-read storage inside the lock: a preceding tab may already have refreshed.
        if (!navigator.locks) throw new Error("Sign-in refresh requires a secure browser context");
        refreshing = navigator.locks
          .request("patient-edge-refresh", async () => {
            syncTokens();
            if (!needsRefresh()) return;
            const current = tokens;
            if (!current?.refresh_token) throw new Error("No refresh token available");
            const response = await fetch(`${EDGE_URL}/auth/refresh`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ refreshToken: current.refresh_token }),
              signal: AbortSignal.timeout(15_000),
            });
            syncTokens();
            // A logout or new login, including in another tab, wins over this request.
            if (tokens !== current) return;
            if (!response.ok) {
              const error = (await response.json().catch(() => undefined)) as { error?: string } | undefined;
              syncTokens();
              if (tokens !== current) return;
              if ([400, 401].includes(response.status) && error?.error === "invalid_grant") session.clear();
              throw new Error("Could not refresh sign-in");
            }
            const next = (await response.json()) as Tokens;
            syncTokens();
            if (tokens === current) session.accept(next, true);
          })
          .then(() => {})
          .finally(() => {
            refreshing = undefined;
          });
      }
      await refreshing;
    }
    return tokens?.access_token;
  },
};
